import type { OrganizationUpdateInputSchema } from "@repo/types";
import type { z } from "zod";
import { db } from "../client";

export async function getOrganizations({
	limit,
	offset,
	query,
	startDate,
	endDate,
}: {
	limit: number;
	offset: number;
	query?: string;
	startDate?: string;
	endDate?: string;
}) {
	return db.organization
		.findMany({
			where: {
				...(query && {
					OR: [
						{ name: { contains: query, mode: "insensitive" } },
						{ slug: { contains: query, mode: "insensitive" } },
					],
				}),
				...(startDate && {
					createdAt: {
						gte: new Date(`${startDate}T00:00:00.000Z`),
						...(endDate && { lte: new Date(`${endDate}T23:59:59.999Z`) }),
					},
				}),
				...(endDate && !startDate && {
					createdAt: {
						lte: new Date(`${endDate}T23:59:59.999Z`),
					},
				}),
			},
			include: {
				members: {
					select: {
						id: true,
					},
				},
				purchases: {
					where: {
						isDeleted: false,
					},
					select: {
						type: true,
						status: true,
						productId: true,
						credit: true,
						startDate: true,
					},
				},
			},
			take: limit,
			skip: offset,
			orderBy: { createdAt: "desc" },
		})
		.then((res) =>
			res.map((org) => ({
				...org,
				membersCount: org.members.length,
			})),
		);
}

export async function countAllOrganizations({
	query,
	startDate,
	endDate,
}: {
	query?: string;
	startDate?: string;
	endDate?: string;
} = {}) {
	return db.organization.count({
		where: {
			...(query && {
				OR: [
					{ name: { contains: query, mode: "insensitive" } },
					{ slug: { contains: query, mode: "insensitive" } },
				],
			}),
			...(startDate && {
				createdAt: {
					gte: new Date(`${startDate}T00:00:00.000Z`),
					...(endDate && { lte: new Date(`${endDate}T23:59:59.999Z`) }),
				},
			}),
			...(endDate && !startDate && {
				createdAt: {
					lte: new Date(`${endDate}T23:59:59.999Z`),
				},
			}),
		},
	});
}

export async function getOrganizationById(id: string) {
	return db.organization.findUnique({
		where: { id },
		include: {
			members: true,
			invitations: true,
		},
	});
}

/**
 * The organization "owner" member and their user row (e.g. for billing contact email).
 * Matches the same owner resolution as other admin org listings (oldest owner by createdAt).
 */
export async function getOrganizationOwnerUser(organizationId: string) {
	const member = await db.member.findFirst({
		where: {
			organizationId,
			role: "owner",
		},
		orderBy: {
			createdAt: "asc",
		},
		select: {
			user: {
				select: {
					id: true,
					name: true,
					email: true,
				},
			},
		},
	});

	return member?.user ?? null;
}

export async function getInvitationById(id: string) {
	return db.invitation.findUnique({
		where: { id },
		include: {
			organization: true,
		},
	});
}

export async function getOrganizationBySlug(slug: string) {
	return db.organization.findUnique({
		where: { slug },
	});
}

export async function getOrganizationMembership(
	organizationId: string,
	userId: string,
) {
	return db.member.findUnique({
		where: {
			organizationId_userId: {
				organizationId,
				userId,
			},
		},
		include: {
			organization: true,
		},
	});
}

export async function getOrganizationWithPurchasesAndMembersCount(
	organizationId: string,
) {
	const organization = await db.organization.findUnique({
		where: {
			id: organizationId,
		},
		include: {
			purchases: true,
			members: {
				select: {
					id: true,
				},
			},
		},
	});

	return organization
		? {
				...organization,
				membersCount: organization.members.length,
			}
		: null;
}

export async function getPendingInvitationByEmail(email: string) {
	return db.invitation.findFirst({
		where: {
			email,
			status: "pending",
		},
	});
}

export async function updateOrganization(
	organization: z.infer<typeof OrganizationUpdateInputSchema> & {
		id: string;
	},
) {
	const id = organization.id;
	const data = {
		...organization,
		id: undefined
	}
	return db.organization.update({
		where: {
			id: organization.id,
		},
		data,
	});
}

export async function getTotalMembers() {
	const result = await db.member.count();
	return result;
}

export async function getAverageMembersPerOrganization() {
	const [totalMembers, totalOrgs] = await Promise.all([
		getTotalMembers(),
		countAllOrganizations(),
	]);
	return totalOrgs > 0 ? totalMembers / totalOrgs : 0;
}

export async function getMemberCountRanges() {
	const organizations = await db.organization.findMany({
		include: {
			members: {
				select: {
					id: true,
				},
			},
		},
	});

	return organizations.reduce(
		(acc, org) => {
			const count = org.members.length;
			if (count === 0) acc.solo++;
			else if (count <= 5) acc.small++;
			else if (count <= 20) acc.medium++;
			else acc.large++;
			return acc;
		},
		{ solo: 0, small: 0, medium: 0, large: 0 },
	);
}

export async function getSubscriptionPlanStats() {
	const organizations = await db.organization.findMany({
		include: {
			purchases: {
				where: {
					isDeleted: false,
					type: "SUBSCRIPTION",
				},
				select: {
					status: true,
					productId: true,
					credit: true,
				},
			},
		},
	});

	const stats = {
		totalOrganizations: organizations.length,
		withActiveSubscription: 0,
		withInactiveSubscription: 0,
		withoutSubscription: 0,
		subscriptionPlans: {} as Record<string, number>,
		totalActiveCredits: 0,
	};

	organizations.forEach((org) => {
		const activeSubscription = org.purchases.find(
			(purchase) => purchase.status === "active"
		);
		
		if (activeSubscription) {
			stats.withActiveSubscription++;
			stats.subscriptionPlans[activeSubscription.productId] = 
				(stats.subscriptionPlans[activeSubscription.productId] || 0) + 1;
			stats.totalActiveCredits += activeSubscription.credit || 0;
		} else if (org.purchases.length > 0) {
			stats.withInactiveSubscription++;
		} else {
			stats.withoutSubscription++;
		}
	});

	return stats;
}
